// Context Gate — Layer 0 of SuPER 2.0
// 5 pre-trade gates; ALL must pass before the trade form unlocks.
// Spec: super_2_system.md → "LAYER 0 — CONTEXT GATE"
import React, { useMemo, useState } from "react";
import { THEME } from "./state";
import Portal from "./Portal";

const GATES = [
  {
    id: "session",
    title: "1. SESSION",
    micro: "What session am I in?",
    options: [
      { v: "London",   label: "London (best)",         pass: true,  hint: "Highest volatility — full execution." },
      { v: "Overlap",  label: "London/NY Overlap",     pass: true,  hint: "Most powerful moves — full execution." },
      { v: "NewYork",  label: "New York",              pass: true,  hint: "Strong continuation — full execution." },
      { v: "Asian",    label: "Asian only",            pass: false, hint: "Reduce size 50% or wait for London open." },
    ],
  },
  {
    id: "htf",
    title: "2. HTF BIAS",
    micro: "Trade WITH the Daily trend — counter-trend = 3× confluences.",
    options: [
      { v: "with",     label: "Aligned with Daily trend",     pass: true,  hint: "✓ Direction matches Daily bias." },
      { v: "counter3", label: "Counter-trend, 3+ confluences", pass: true,  hint: "Risk‑on. Tight stop, half size." },
      { v: "counter",  label: "Counter-trend, < 3 confluences", pass: false, hint: "Wait. Counter-trend without 3 confluences = gambling." },
      { v: "unclear",  label: "Bias unclear / chop",          pass: false, hint: "No trade. Re-analyse Weekly + Daily." },
    ],
  },
  {
    id: "news",
    title: "3. NEWS",
    micro: "High-impact event in the next 2 hours?",
    options: [
      { v: "clear",    label: "No red events nearby",         pass: true,  hint: "✓ Full SuPER execution." },
      { v: "orange",   label: "Orange event soon",            pass: true,  hint: "Reduce size · widen stops slightly." },
      { v: "red30",    label: "Red event within 30 min",      pass: false, hint: "NO new entries 30 min before/after red." },
    ],
  },
  {
    id: "account",
    title: "4. ACCOUNT HEALTH",
    micro: "Numbers before charts. Always.",
    options: [
      { v: "fresh",    label: "Healthy · within daily loss buffer",  pass: true,  hint: "✓ Normal sizing OK." },
      { v: "won2",     label: "Already up 2%+ today",                 pass: true,  hint: "Reduce to half size — protect the green." },
      { v: "streak",   label: "3+ losing streak",                     pass: true,  hint: "Drop to 0.5% risk until streak breaks." },
      { v: "near4",    label: "Near 4% daily loss",                   pass: false, hint: "Stop. 4% is the circuit breaker (2% buffer to limit)." },
    ],
  },
  {
    id: "mindset",
    title: "5. MINDSET",
    micro: 'The most-skipped gate. "Calm and neutral" or wait.',
    options: [
      { v: "calm",     label: "Calm · neutral · slept enough", pass: true,  hint: "✓ State management — proceed." },
      { v: "fomo",     label: "FOMO / fear of missing",         pass: false, hint: "Do not enter. The market will be there tomorrow." },
      { v: "tilt",     label: "Reacting to last trade",         pass: false, hint: "Walk. 15 min minimum. Then re-evaluate." },
      { v: "tired",    label: "Tired or stressed",              pass: false, hint: "Sleep affects risk decisions. Skip today." },
    ],
  },
];

const TOTAL = GATES.length;

export default function ContextGate({ onPass, onClose }) {
  const [picks, setPicks] = useState({});  // { gateId: optionValue }

  const passes = useMemo(() => {
    return GATES.map((g) => {
      const v = picks[g.id];
      const opt = g.options.find((o) => o.v === v);
      return { id: g.id, v, opt, pass: !!opt?.pass };
    });
  }, [picks]);

  const passedCount = passes.filter((p) => p.pass).length;
  const allAnswered = passes.every((p) => !!p.v);
  const allPass = passes.every((p) => p.pass);
  const ready = allAnswered && allPass;

  const pickOpt = (gateId, v) => setPicks((p) => ({ ...p, [gateId]: v }));

  const proceed = () => {
    if (!ready) return;
    const summary = passes.reduce((acc, p) => {
      acc[p.id] = p.v;
      return acc;
    }, {});
    onPass?.(summary);
  };

  return (
    <Portal>
      <div data-testid="context-gate-modal"
        style={{
          position: "fixed", inset: 0, zIndex: 11000,
          background: "rgba(0,0,0,0.86)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "flex-start", justifyContent: "center",
          padding: "24px 16px", overflowY: "auto",
        }}>
        <div style={{
          width: "min(820px, 100%)", background: "#0C0C0C",
          border: `1px solid ${THEME.gold}55`, borderRadius: 14,
          boxShadow: `0 0 80px rgba(201,162,39,0.18)`,
          padding: 22, marginTop: 24,
        }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: 2.5, color: THEME.gold, fontFamily: "monospace" }}>LAYER 0 · CONTEXT GATE</div>
              <div style={{ color: THEME.text, fontSize: 22, fontWeight: 800, marginTop: 4, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 0.5 }}>
                RUN SuPER — Pre-Trade Unlock
              </div>
              <div style={{ color: THEME.mute, fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                Pass all 5 gates to unlock the trade form. ~2 minutes that prevents 80% of impulsive trades.
              </div>
            </div>
            <button data-testid="context-gate-close" onClick={onClose}
              style={{ background: "transparent", border: `1px solid ${THEME.border}`, color: THEME.mute, fontSize: 12, padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontFamily: "monospace" }}>Cancel</button>
          </div>

          {/* Gate progress */}
          <div data-testid="context-gate-progress" style={{ display: "flex", gap: 6, marginBottom: 18 }}>
            {GATES.map((g, i) => {
              const p = passes[i];
              const color = !p.v ? THEME.border : (p.pass ? "#22C55E" : "#FF4444");
              return (
                <div key={g.id} style={{
                  flex: 1, height: 6, borderRadius: 3, background: color,
                  boxShadow: p.pass ? `0 0 8px ${color}88` : "none",
                  transition: "background 0.2s",
                }} />
              );
            })}
          </div>

          {/* Gate list */}
          <div style={{ display: "grid", gap: 12 }}>
            {GATES.map((g, idx) => (
              <GateRow key={g.id} gate={g} pick={picks[g.id]} onPick={(v) => pickOpt(g.id, v)} status={passes[idx]} />
            ))}
          </div>

          {/* Footer */}
          <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div data-testid="context-gate-status" style={{ fontFamily: "monospace", fontSize: 12, letterSpacing: 1, color: ready ? "#22C55E" : THEME.mute }}>
              {ready ? "✅ SuPER READY — TRADE UNLOCKED"
                : !allAnswered ? `${passedCount}/${TOTAL} answered · keep going`
                : "❌ NOT YET SuPER — review the failing gates above"}
            </div>
            <button data-testid="context-gate-proceed-btn"
              disabled={!ready} onClick={proceed}
              style={{
                padding: "12px 22px", borderRadius: 8,
                background: ready ? `linear-gradient(135deg, ${THEME.gold}, #E8B94F)` : THEME.border,
                color: ready ? "#0a0a0a" : THEME.mute,
                border: "none", cursor: ready ? "pointer" : "not-allowed",
                fontWeight: 800, fontFamily: "monospace", letterSpacing: 1.5, fontSize: 12, textTransform: "uppercase",
                boxShadow: ready ? "0 0 24px rgba(201,162,39,0.35)" : "none",
                transition: "all 0.2s",
              }}>
              {ready ? "Proceed to Plan →" : "Pass All Gates First"}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

function GateRow({ gate, pick, onPick, status }) {
  const opt = gate.options.find((o) => o.v === pick);
  const fillColor = !pick ? THEME.border : (opt?.pass ? "#22C55E" : "#FF4444");
  return (
    <div data-testid={`context-gate-row-${gate.id}`}
      style={{
        background: "#13161c", border: `1px solid ${pick ? fillColor + "55" : THEME.border}`,
        borderLeft: `3px solid ${fillColor}`,
        borderRadius: 10, padding: 14, transition: "border 0.2s",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "monospace", fontSize: 11, letterSpacing: 2, color: THEME.gold, fontWeight: 700 }}>{gate.title}</div>
        {pick && (
          <div data-testid={`context-gate-${gate.id}-status`} style={{
            fontSize: 10, letterSpacing: 1.5, fontFamily: "monospace", fontWeight: 800,
            color: status.pass ? "#22C55E" : "#FF4444",
          }}>{status.pass ? "✓ PASS" : "✗ FAIL"}</div>
        )}
      </div>
      <div style={{ color: THEME.mute, fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>{gate.micro}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {gate.options.map((o) => (
          <button key={o.v} type="button" data-testid={`context-gate-${gate.id}-opt-${o.v}`}
            onClick={() => onPick(o.v)}
            style={{
              padding: "8px 12px", borderRadius: 6,
              background: pick === o.v ? (o.pass ? "#22C55E22" : "#FF444422") : "transparent",
              border: `1px solid ${pick === o.v ? (o.pass ? "#22C55E" : "#FF4444") : THEME.border}`,
              color: pick === o.v ? (o.pass ? "#22C55E" : "#FF4444") : THEME.text,
              fontSize: 12, cursor: "pointer", fontFamily: "Inter, sans-serif",
              transition: "all 0.15s",
            }}>{o.label}</button>
        ))}
      </div>
      {pick && (
        <div data-testid={`context-gate-${gate.id}-hint`} style={{
          marginTop: 10, fontSize: 12, color: status.pass ? THEME.text : "#FF8888",
          fontStyle: "italic", lineHeight: 1.5, paddingLeft: 10, borderLeft: `2px solid ${fillColor}`,
        }}>{opt?.hint}</div>
      )}
    </div>
  );
}
